import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Save, Plus, Trash, RefreshCw, Info, ArrowLeft } from 'lucide-react';
import { gradientText } from '../theme';
import LoadingSpinner from '../components/LoadingSpinner';

// Types pour les paramètres
interface AppSetting {
  id: string;
  key: string;
  value: any;
  description: string;
  created_at: string;
  updated_at: string;
}

// Type pour les ressources d'aide
interface HelpResource {
  id: string;
  type: 'tips' | 'contact' | 'donation' | 'privacy_policy' | 'terms_of_service';
  title: string;
  content: string;
  link: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Type pour la configuration du bouton d'aide
interface HelpButtonConfig {
  id: string;
  animation_enabled: boolean;
  created_at: string;
  updated_at: string;
}

type PriceSettings = {
  amount: number;
  currency: string;
  enabled?: boolean;
};

type PaymentGateway = {
  cinetpay_enabled: boolean;
  lygos_enabled: boolean;
  alternative_url: string;
};

type MobilePaymentNumber = {
  number: string;
  reference: string;
};

type MobilePaymentNumbers = {
  orange_money: MobilePaymentNumber[];
  mtn_money: MobilePaymentNumber[];
};

const AdminSettings: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // États pour les paramètres édités
  const [relationDeletionPrice, setRelationDeletionPrice] = useState<PriceSettings>({ amount: 0, currency: 'USD' });
  const [relationUnlockPrice, setRelationUnlockPrice] = useState<PriceSettings>({ amount: 0, currency: 'USD' });
  const [shortTermSearchUnlockPrice, setShortTermSearchUnlockPrice] = useState<PriceSettings>({ amount: 0, currency: 'USD', enabled: true });
  const [referralCommissionRate, setReferralCommissionRate] = useState<number>(10);
  const [paymentGateway, setPaymentGateway] = useState<PaymentGateway>({ 
    cinetpay_enabled: true, 
    lygos_enabled: true, 
    alternative_url: '' 
  });
  const [mobilePaymentNumbers, setMobilePaymentNumbers] = useState<MobilePaymentNumbers>({
    orange_money: [{ number: '', reference: '' }],
    mtn_money: [{ number: '', reference: '' }]
  });
  
  // États pour les ressources d'aide
  const [helpResources, setHelpResources] = useState<HelpResource[]>([]);
  const [selectedResource, setSelectedResource] = useState<HelpResource | null>(null);
  const [editingResource, setEditingResource] = useState<HelpResource | null>(null);
  
  // État pour la configuration du bouton d'aide
  const [helpButtonConfig, setHelpButtonConfig] = useState<HelpButtonConfig | null>(null);
  const [animationEnabled, setAnimationEnabled] = useState(false);

  // Charger les paramètres depuis Supabase
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        
        // Charger les paramètres de l'application
        const { data, error } = await supabase
          .from('app_settings')
          .select('*');
        
        if (error) throw error;
        
        console.log('Paramètres chargés:', data);
        
        // Initialiser les états avec les valeurs des paramètres
        data.forEach((setting: AppSetting) => {
          switch (setting.key) {
            case 'relation_deletion_price':
              setRelationDeletionPrice(setting.value);
              break;
            case 'relation_unlock_price':
              setRelationUnlockPrice(setting.value);
              break;
            case 'short_term_search_unlock_price':
              setShortTermSearchUnlockPrice(setting.value);
              break;
            case 'payment_gateway':
              setPaymentGateway(setting.value);
              break;
            case 'mobile_payment_numbers':
              setMobilePaymentNumbers(setting.value);
              break;
            case 'referral_commission_rate':
              setReferralCommissionRate(setting.value);
              break;
          }
        });
        
        // Charger les ressources d'aide
        await loadHelpResources();
        
        // Charger la configuration du bouton d'aide
        await loadHelpButtonConfig();
        
      } catch (err) {
        console.error('Erreur lors du chargement des paramètres:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      } finally {
        setLoading(false);
      }
    };
    
    loadSettings();
  }, []);
  
  // Charger les ressources d'aide
  const loadHelpResources = async () => {
    try {
      const { data, error } = await supabase
        .from('help_resources')
        .select('*')
        .order('updated_at', { ascending: false });
      
      if (error) throw error;
      
      console.log('Ressources d\'aide chargées:', data);
      setHelpResources(data || []);
    } catch (err) {
      console.error('Erreur lors du chargement des ressources d\'aide:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    }
  };
  
  // Charger la configuration du bouton d'aide
  const loadHelpButtonConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('help_button_config')
        .select('*')
        .single();
      
      if (error) {
        console.error('Erreur lors du chargement de la configuration du bouton d\'aide:', error);
        return;
      }
      
      console.log('Configuration du bouton d\'aide chargée:', data);
      setHelpButtonConfig(data);
      setAnimationEnabled(data?.animation_enabled || false);
    } catch (err) {
      console.error('Erreur lors du chargement de la configuration du bouton d\'aide:', err);
    }
  };
  
  // Sauvegarder tous les paramètres
  const saveAllSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Préparer les mises à jour
      const updates = [
        { key: 'relation_deletion_price', value: relationDeletionPrice, description: 'Prix pour supprimer une relation' },
        { key: 'relation_unlock_price', value: relationUnlockPrice, description: 'Prix pour déverrouiller les détails d\'une relation' },
        { key: 'short_term_search_unlock_price', value: shortTermSearchUnlockPrice, description: 'Prix pour déverrouiller les résultats de relations court terme dans la recherche' },
        { key: 'payment_gateway', value: paymentGateway, description: 'Configuration de la passerelle de paiement' },
        { key: 'mobile_payment_numbers', value: mobilePaymentNumbers, description: 'Numéros pour paiements mobile money' },
        { key: 'referral_commission_rate', value: Number(referralCommissionRate), description: 'Taux de commission sur les parrainages' }
      ];
      
      // Effectuer les mises à jour une par une
      for (const update of updates) {
        const { error } = await supabase
          .from('app_settings')
          .upsert({ 
            key: update.key, 
            value: update.value, 
            description: update.description,
            updated_at: new Date().toISOString()
          }, { 
            onConflict: 'key'
          });
        
        if (error) throw error;
      }
      
      setSuccess('Tous les paramètres ont été sauvegardés avec succès.');
      
      // Rafraîchir la liste des paramètres
      const { error } = await supabase
        .from('app_settings')
        .select('*');
      
      if (error) throw error;
      
    } catch (err) {
      console.error('Erreur lors de la sauvegarde des paramètres:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
      
      // Effacer le message de succès après 3 secondes
      if (success) {
        setTimeout(() => setSuccess(null), 3000);
      }
    }
  };
  
  // Ajouter un numéro pour Orange Money
  const addOrangeMoneyNumber = () => {
    setMobilePaymentNumbers(prev => ({
      ...prev,
      orange_money: [...prev.orange_money, { number: '', reference: '' }]
    }));
  };
  
  // Ajouter un numéro pour MTN Money
  const addMtnMoneyNumber = () => {
    setMobilePaymentNumbers(prev => ({
      ...prev,
      mtn_money: [...prev.mtn_money, { number: '', reference: '' }]
    }));
  };
  
  // Supprimer un numéro Orange Money
  const removeOrangeMoneyNumber = (index: number) => {
    setMobilePaymentNumbers(prev => ({
      ...prev,
      orange_money: prev.orange_money.filter((_, i) => i !== index)
    }));
  };
  
  // Supprimer un numéro MTN Money
  const removeMtnMoneyNumber = (index: number) => {
    setMobilePaymentNumbers(prev => ({
      ...prev,
      mtn_money: prev.mtn_money.filter((_, i) => i !== index)
    }));
  };
  
  // Mettre à jour un numéro Orange Money
  const updateOrangeMoneyNumber = (index: number, field: 'number' | 'reference', value: string) => {
    setMobilePaymentNumbers(prev => {
      const newOrangeMoney = [...prev.orange_money];
      newOrangeMoney[index] = { ...newOrangeMoney[index], [field]: value };
      return { ...prev, orange_money: newOrangeMoney };
    });
  };
  
  // Mettre à jour un numéro MTN Money
  const updateMtnMoneyNumber = (index: number, field: 'number' | 'reference', value: string) => {
    setMobilePaymentNumbers(prev => {
      const newMtnMoney = [...prev.mtn_money];
      newMtnMoney[index] = { ...newMtnMoney[index], [field]: value };
      return { ...prev, mtn_money: newMtnMoney };
    });
  };
  
  // Sélectionner une ressource pour l'édition
  const selectResourceForEdit = (resource: HelpResource) => {
    setEditingResource({ ...resource });
    setSelectedResource(resource);
  };
  
  // Sauvegarder une ressource d'aide
  const saveHelpResource = async () => {
    if (!editingResource) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const isNew = !editingResource.id;
      
      if (isNew) {
        // Créer une nouvelle ressource
        const { error } = await supabase
          .from('help_resources')
          .insert([{ 
            type: editingResource.type,
            title: editingResource.title,
            content: editingResource.content,
            link: editingResource.link,
            is_active: editingResource.is_active
          }]);
        
        if (error) throw error;
        setSuccess('Ressource créée avec succès');
      } else {
        // Mettre à jour une ressource existante
        const { error } = await supabase
          .from('help_resources')
          .update({ 
            type: editingResource.type,
            title: editingResource.title,
            content: editingResource.content,
            link: editingResource.link,
            is_active: editingResource.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingResource.id);
        
        if (error) throw error;
        setSuccess('Ressource mise à jour avec succès');
      }
      
      // Rafraîchir la liste des ressources
      await loadHelpResources();
      
      // Réinitialiser l'état d'édition
      setEditingResource(null);
      setSelectedResource(null);
      
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de la ressource:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
      
      // Effacer le message de succès après 3 secondes
      if (success) {
        setTimeout(() => setSuccess(null), 3000);
      }
    }
  };
  
  // Supprimer une ressource d'aide
  const deleteHelpResource = async (id: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette ressource ?')) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      const { error } = await supabase
        .from('help_resources')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // Rafraîchir la liste des ressources
      await loadHelpResources();
      
      setSuccess('Ressource supprimée avec succès');
      
      // Réinitialiser l'état d'édition si la ressource supprimée était en cours d'édition
      if (selectedResource?.id === id) {
        setEditingResource(null);
        setSelectedResource(null);
      }
      
    } catch (err) {
      console.error('Erreur lors de la suppression de la ressource:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
    }
  };
  
  // Créer une nouvelle ressource d'aide
  const createNewResource = (type: HelpResource['type']) => {
    const newResource: HelpResource = {
      id: '',
      type,
      title: '',
      content: '',
      link: '',
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    setEditingResource(newResource);
    setSelectedResource(null);
  };
  
  // Mettre à jour la configuration du bouton d'aide
  const toggleHelpButtonAnimation = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const newValue = !animationEnabled;
      
      if (helpButtonConfig) {
        const { error } = await supabase
          .from('help_button_config')
          .update({ 
            animation_enabled: newValue,
            updated_at: new Date().toISOString()
          })
          .eq('id', helpButtonConfig.id);
        
        if (error) throw error;
      } else {
        // Si aucune configuration n'existe, en créer une nouvelle
        const { error } = await supabase
          .from('help_button_config')
          .insert([{ animation_enabled: newValue }]);
        
        if (error) throw error;
      }
      
      // Mettre à jour l'état local
      setAnimationEnabled(newValue);
      setSuccess(`Animation du bouton d'aide ${newValue ? 'activée' : 'désactivée'}`);
      
      // Recharger la configuration
      await loadHelpButtonConfig();
      
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la configuration du bouton d\'aide:', err);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setSaving(false);
      
      // Effacer le message de succès après 3 secondes
      if (success) {
        setTimeout(() => setSuccess(null), 3000);
      }
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <LoadingSpinner size="large" fullScreen={false} text={t('common.loading')} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-black">
      {/* En-tête */}
      <header className="bg-gradient-to-r from-primary-start/10 to-primary-end/10 border-b border-primary-start/20">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold">
              <span className={gradientText}>{t('admin.dashboard.sections.settings')}</span>
            </h1>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">{t('admin.dashboard.returnToDashboard')}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Contenu principal */}
      <main className="container mx-auto px-4 py-6">
        {/* Messages d'erreur ou de succès */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-red-500" />
            <p>{error}</p>
          </div>
        )}
        
        {success && (
          <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6 text-white flex items-center gap-2">
            <Info className="w-5 h-5 text-green-500" />
            <p>{success}</p>
          </div>
        )}
        
        {/* Section des prix */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">{t('admin.settings.featurePricing')}</h2>
          
          <div className="space-y-6">
            {/* Prix pour supprimer une relation */}
            <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
              <h3 className="text-white mb-4">{t('admin.settings.relationDeletionPrice')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.amount')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={relationDeletionPrice.amount}
                    onChange={(e) => setRelationDeletionPrice({ ...relationDeletionPrice, amount: parseFloat(e.target.value) })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.currency')}</label>
                  <select
                    value={relationDeletionPrice.currency}
                    onChange={(e) => setRelationDeletionPrice({ ...relationDeletionPrice, currency: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="XAF">XAF (FCFA)</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Prix pour déverrouiller les détails d'une relation */}
            <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
              <h3 className="text-white mb-4">{t('admin.settings.relationUnlockPrice')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.amount')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={relationUnlockPrice.amount}
                    onChange={(e) => setRelationUnlockPrice({ ...relationUnlockPrice, amount: parseFloat(e.target.value) })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.currency')}</label>
                  <select
                    value={relationUnlockPrice.currency}
                    onChange={(e) => setRelationUnlockPrice({ ...relationUnlockPrice, currency: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="XAF">XAF (FCFA)</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Prix pour déverrouiller les résultats de relations court terme */}
            <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
              <h3 className="text-white mb-4">{t('admin.settings.shortTermSearchUnlockPrice')}</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.amount')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={shortTermSearchUnlockPrice.amount}
                    onChange={(e) => setShortTermSearchUnlockPrice({ ...shortTermSearchUnlockPrice, amount: parseFloat(e.target.value) })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.currency')}</label>
                  <select
                    value={shortTermSearchUnlockPrice.currency}
                    onChange={(e) => setShortTermSearchUnlockPrice({ ...shortTermSearchUnlockPrice, currency: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="XAF">XAF (FCFA)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.enabled')}</label>
                  <div className="flex items-center mt-4">
                    <input
                      type="checkbox"
                      checked={shortTermSearchUnlockPrice.enabled}
                      onChange={(e) => setShortTermSearchUnlockPrice({ ...shortTermSearchUnlockPrice, enabled: e.target.checked })}
                      className="w-5 h-5 bg-gray-800 border border-gray-700 rounded text-primary-end focus:ring-primary-end"
                    />
                    <span className="ml-2 text-white">
                      {shortTermSearchUnlockPrice.enabled ? 'Activé' : 'Désactivé'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Nouvelle section pour le taux de commission sur les parrainages */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Commission de parrainage</h2>
          <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
            <h3 className="text-white mb-4">Taux de commission sur les parrainages</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-400 mb-2">Pourcentage (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={referralCommissionRate}
                  onChange={(e) => setReferralCommissionRate(parseFloat(e.target.value))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Pourcentage que reçoit l'admin qui a parrainé un utilisateur</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Section de configuration des passerelles de paiement */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">{t('admin.settings.paymentGateway')}</h2>
          <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-white mb-4">{t('admin.settings.gateways')}</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={paymentGateway.cinetpay_enabled}
                      onChange={(e) => setPaymentGateway({ ...paymentGateway, cinetpay_enabled: e.target.checked })}
                      className="w-5 h-5 bg-gray-800 border border-gray-700 rounded text-primary-end focus:ring-primary-end"
                    />
                    <span className="ml-2 text-white">CinetPay</span>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={paymentGateway.lygos_enabled}
                      onChange={(e) => setPaymentGateway({ ...paymentGateway, lygos_enabled: e.target.checked })}
                      className="w-5 h-5 bg-gray-800 border border-gray-700 rounded text-primary-end focus:ring-primary-end"
                    />
                    <span className="ml-2 text-white">Lygos</span>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-gray-400 mb-2">{t('admin.settings.alternativeUrl')}</label>
                <input
                  type="text"
                  value={paymentGateway.alternative_url}
                  onChange={(e) => setPaymentGateway({ ...paymentGateway, alternative_url: e.target.value })}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  placeholder="https://pay.example.com/$bodycount"
                />
                <p className="text-xs text-gray-500 mt-1">{t('admin.settings.alternativeUrlDescription')}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Section des numéros de paiement mobile */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">{t('admin.settings.mobilePaymentNumbers')}</h2>
          
          {/* Orange Money */}
          <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white">{t('admin.settings.orangeMoney')}</h3>
              <button
                onClick={addOrangeMoneyNumber}
                className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                <span>{t('admin.settings.add')}</span>
              </button>
            </div>
            
            {mobilePaymentNumbers.orange_money.length === 0 ? (
              <p className="text-gray-400">{t('admin.settings.noNumbersConfigured')}</p>
            ) : (
              <div className="space-y-4">
                {mobilePaymentNumbers.orange_money.map((number, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center border-b border-gray-800 pb-4">
                    <div>
                      <label className="block text-gray-400 mb-2">{t('admin.settings.number')}</label>
                      <input
                        type="text"
                        value={number.number}
                        onChange={(e) => updateOrangeMoneyNumber(index, 'number', e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                        placeholder="+237 6XXXXXXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2">{t('admin.settings.reference')}</label>
                      <input
                        type="text"
                        value={number.reference}
                        onChange={(e) => updateOrangeMoneyNumber(index, 'reference', e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                        placeholder="Mycount + $userName"
                      />
                    </div>
                    <div className="flex items-end justify-end h-full">
                      <button
                        onClick={() => removeOrangeMoneyNumber(index)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* MTN Money */}
          <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white">{t('admin.settings.mtnMoney')}</h3>
              <button
                onClick={addMtnMoneyNumber}
                className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 py-1 rounded-lg text-sm flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                <span>{t('admin.settings.add')}</span>
              </button>
            </div>
            
            {mobilePaymentNumbers.mtn_money.length === 0 ? (
              <p className="text-gray-400">{t('admin.settings.noNumbersConfigured')}</p>
            ) : (
              <div className="space-y-4">
                {mobilePaymentNumbers.mtn_money.map((number, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center border-b border-gray-800 pb-4">
                    <div>
                      <label className="block text-gray-400 mb-2">{t('admin.settings.number')}</label>
                      <input
                        type="text"
                        value={number.number}
                        onChange={(e) => updateMtnMoneyNumber(index, 'number', e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                        placeholder="+237 6XXXXXXXX"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 mb-2">{t('admin.settings.reference')}</label>
                      <input
                        type="text"
                        value={number.reference}
                        onChange={(e) => updateMtnMoneyNumber(index, 'reference', e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                        placeholder="Mycount + $userName"
                      />
                    </div>
                    <div className="flex items-end justify-end h-full">
                      <button
                        onClick={() => removeMtnMoneyNumber(index)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <Trash className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {/* Section de configuration du bouton d'aide */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">{t('admin.settings.helpButton')}</h2>
          
          <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-white mb-2">{t('admin.settings.animation')}</h3>
                <p className="text-gray-400 text-sm">{t('admin.settings.helpButtonDescription')}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-400">{animationEnabled ? 'Activée' : 'Désactivée'}</span>
                <button
                  onClick={toggleHelpButtonAnimation}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    animationEnabled ? 'bg-primary-end' : 'bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      animationEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        {/* Section des ressources d'aide */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">{t('admin.settings.helpResources')}</h2>
          
          {/* Boutons pour créer de nouvelles ressources */}
          <div className="flex flex-wrap gap-2 mb-6">
            <button
              onClick={() => createNewResource('tips')}
              className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 py-1 rounded-lg text-sm"
            >
              + {t('admin.settings.tips')}
            </button>
            <button
              onClick={() => createNewResource('contact')}
              className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 py-1 rounded-lg text-sm"
            >
              + {t('admin.settings.contact')}
            </button>
            <button
              onClick={() => createNewResource('donation')}
              className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 py-1 rounded-lg text-sm"
            >
              + {t('admin.settings.don')}
            </button>
            <button
              onClick={() => createNewResource('privacy_policy')}
              className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 py-1 rounded-lg text-sm"
            >
              + {t('admin.settings.privacyPolicy')}
            </button>
            <button
              onClick={() => createNewResource('terms_of_service')}
              className="bg-gradient-to-r from-primary-start to-primary-end text-white px-3 py-1 rounded-lg text-sm"
            >
              + {t('admin.settings.termsOfService')}
            </button>
          </div>
          
          {/* Liste des ressources existantes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {helpResources.map((resource) => (
              <div 
                key={resource.id} 
                className={`bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-4 cursor-pointer border-2 ${
                  selectedResource?.id === resource.id ? 'border-primary-end' : 'border-transparent'
                }`}
                onClick={() => selectResourceForEdit(resource)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <span className={`px-2 py-1 text-xs rounded-full mr-2 ${resource.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {resource.is_active ? 'Actif' : 'Inactif'}
                    </span>
                    <span className="px-2 py-1 bg-primary-end/20 text-primary-end text-xs rounded-full">
                      {resource.type === 'tips' && 'Conseils'}
                      {resource.type === 'contact' && 'Contact'}
                      {resource.type === 'donation' && 'Don'}
                      {resource.type === 'privacy_policy' && 'Politique'}
                      {resource.type === 'terms_of_service' && 'Termes'}
                    </span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteHelpResource(resource.id);
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </div>
                <h3 className="text-white font-medium truncate">{resource.title || '(Sans titre)'}</h3>
                <p className="text-gray-400 text-sm truncate">{resource.content?.slice(0, 50)}...</p>
                <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                  <span>
                    {t('admin.settings.modified')} {new Date(resource.updated_at).toLocaleDateString()}
                  </span>
                  {resource.link && (
                    <span className="text-primary-end">Lien: {resource.link.slice(0, 20)}...</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Zone d'édition de la ressource sélectionnée */}
          {editingResource && (
            <div className="bg-gradient-to-r from-primary-start/5 to-primary-end/5 rounded-xl p-6">
              <h3 className="text-white font-medium mb-4">
                {editingResource.id ? t('admin.settings.editResource') : t('admin.settings.newResource')}
              </h3>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Type */}
                  <div>
                    <label className="block text-gray-400 mb-2">{t('admin.settings.type')}</label>
                    <select
                      value={editingResource.type}
                      onChange={(e) => setEditingResource({ ...editingResource, type: e.target.value as HelpResource['type'] })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                    >
                      <option value="tips">{t('admin.settings.tips')}</option>
                      <option value="contact">{t('admin.settings.contact')}</option>
                      <option value="donation">{t('admin.settings.don')}</option>
                      <option value="privacy_policy">{t('admin.settings.privacyPolicy')}</option>
                      <option value="terms_of_service">{t('admin.settings.termsOfService')}</option>
                    </select>
                  </div>
                  
                  {/* Actif */}
                  <div>
                    <label className="block text-gray-400 mb-2">{t('admin.settings.status')}</label>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        checked={editingResource.is_active}
                        onChange={(e) => setEditingResource({ ...editingResource, is_active: e.target.checked })}
                        className="w-5 h-5 bg-gray-800 border border-gray-700 rounded text-primary-end focus:ring-primary-end"
                      />
                      <span className="ml-2 text-white">
                        {editingResource.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Titre */}
                <div>
                  <label className="block text-gray-400 mb-2">{t('admin.settings.title')}</label>
                  <input
                    type="text"
                    value={editingResource.title}
                    onChange={(e) => setEditingResource({ ...editingResource, title: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                
                {/* Lien (pour les types qui en ont besoin) */}
                {(editingResource.type === 'tips' || editingResource.type === 'contact' || editingResource.type === 'donation') && (
                  <div>
                    <label className="block text-gray-400 mb-2">{t('admin.settings.link')}</label>
                    <input
                      type="text"
                      value={editingResource.link || ''}
                      onChange={(e) => setEditingResource({ ...editingResource, link: e.target.value })}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white"
                      placeholder="https://..."
                    />
                  </div>
                )}
                
                {/* Contenu (pour les politiques et termes, ou descriptions) */}
                <div>
                  <label className="block text-gray-400 mb-2">
                    {(editingResource.type === 'privacy_policy' || editingResource.type === 'terms_of_service') 
                      ? 'Contenu (Markdown supporté)' 
                      : t('admin.settings.description')}
                  </label>
                  <textarea
                    value={editingResource.content || ''}
                    onChange={(e) => setEditingResource({ ...editingResource, content: e.target.value })}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white min-h-[200px]"
                    placeholder={editingResource.type === 'privacy_policy' || editingResource.type === 'terms_of_service' 
                      ? '# Titre\n\nContenu en markdown...' 
                      : t('admin.settings.enterDescription')}
                  />
                </div>
                
                {/* Boutons d'action */}
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => {
                      setEditingResource(null);
                      setSelectedResource(null);
                    }}
                    className="px-4 py-2 bg-gray-700 text-white rounded-lg"
                  >
                    {t('admin.settings.cancel')}
                  </button>
                  <button
                    onClick={saveHelpResource}
                    disabled={saving}
                    className="bg-gradient-to-r from-primary-start to-primary-end text-white px-4 py-2 rounded-lg flex items-center gap-2"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    <span>{saving ? t('admin.settings.saving') : t('admin.settings.save')}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Bouton de sauvegarde en bas de page */}
        <div className="fixed bottom-6 right-6 z-10 sm:static sm:mt-8 sm:flex sm:justify-center">
          <button
            onClick={saveAllSettings}
            disabled={saving}
            className="bg-gradient-to-r from-primary-start to-primary-end text-white px-6 py-3 rounded-lg flex items-center gap-2 shadow-lg hover:opacity-90 transition-opacity"
          >
            {saving ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            <span className="font-medium">{saving ? t('admin.settings.saving') : t('admin.settings.saveAll')}</span>
          </button>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings; 